import { useEffect, useRef } from "react";

interface PushToSummitConfirmDialogProps {
  open: boolean;
  /** Custom body text. Defaults to single-meeting wording. */
  message?: string;
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Mirrors Blazor's "Confirm Push to Summit" TelerikWindow.
 * Shows a modal asking the user to confirm before pushing meeting(s) to Summit.
 */
export function PushToSummitConfirmDialog({
  open,
  message = "Are you sure you want to push this meeting to Summit?",
  submitting = false,
  onCancel,
  onConfirm
}: PushToSummitConfirmDialogProps) {
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      requestAnimationFrame(() => confirmBtnRef.current?.focus());
    } else {
      previousFocusRef.current?.focus();
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onCancel}
      onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}
      tabIndex={-1}
      aria-hidden="true"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="push-to-summit-title"
        className="w-[450px] max-w-[calc(100vw-2rem)] border border-slate-300 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — mirrors WindowTitle + Close action */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-black px-4 py-3">
          <h2 id="push-to-summit-title" className="text-base font-semibold text-white">
            Confirm Push to Summit
          </h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="text-white hover:text-slate-300"
          >
            &#x2715;
          </button>
        </div>

        {/* Content — mirrors WindowContent */}
        <div className="px-5 py-5 text-center text-sm text-slate-700">
          <p>{message}</p>
        </div>

        {/* Footer — mirrors WindowFooter with Go Back / Confirm buttons */}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="Add-Cancel-Button border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Go Back
          </button>
          <button
            type="button"
            ref={confirmBtnRef}
            onClick={onConfirm}
            disabled={submitting}
            className="Add-Submit-Button border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {submitting ? "Pushing\u2026" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
